import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend);

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const monthNamesPL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

const parseAmount = (val) => {
  if (!val) return 0;
  return parseFloat(val.toString().replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;
};

const DailyPurchaseComparisonChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);
  const [availableFilters, setAvailableFilters] = useState({ months: [], years: [] });
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    const months = new Set();
    const years = new Set();
    const monthYearTotals = {};

    data.forEach((row) => {
      const date = new Date(row.DataZakupu);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const amount = parseAmount(row.K_42);
      const key = `${year}-${month}`;

      if (!isNaN(year) && !isNaN(month)) {
        months.add(month);
        years.add(year);
        if (!monthYearTotals[key]) monthYearTotals[key] = 0;
        monthYearTotals[key] += amount;
      }
    });

    const sortedYears = Array.from(years).sort();
    const sortedMonths = Array.from(months).sort();

    setAvailableFilters({ months: sortedMonths, years: sortedYears });

    const latest = Object.entries(monthYearTotals)
      .filter(([_, value]) => value > 0)
      .sort((a, b) => new Date(b[0]) - new Date(a[0]))[0];

    if (latest) {
      const [y, m] = latest[0].split("-").map(Number);
      setSelectedYear(y);
      setSelectedMonth(m);
    }
  }, [data]);

  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const current = {};
    const previous = {};

    data.forEach((row) => {
      const date = new Date(row.DataZakupu);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const amount = parseAmount(row.K_42);

      if (month === selectedMonth) {
        const target = year === selectedYear ? current : year === selectedYear - 1 ? previous : null;
        if (target) {
          if (!target[day]) target[day] = 0;
          target[day] += amount;
        }
      }
    });

    const currentData = days.map((d) => current[d] || 0);
    const previousData = days.map((d) => previous[d] || 0);

    const max = Math.max(...currentData);
    const min = Math.min(...currentData.filter((v) => v > 0));

    const barColors = currentData.map((v) => {
      if (v === max) return "#9848c7";
      if (v === min) return "rgba(152, 72, 199, 0.3)";
      return "rgba(152, 72, 199, 0.5)";
    });

    setChartData({
      labels: days.map((d) => `${d}/${selectedMonth.toString().padStart(2, "0")}`),
      datasets: [
        {
          label: `Zakupy (${selectedYear})`,
          data: currentData,
          backgroundColor: barColors,
          borderColor: "#9848c7",
          type: "bar",
        },
        {
          label: `Trend (${selectedYear})`,
          data: currentData,
          type: "line",
          borderColor: "#9848c7",
          backgroundColor: "rgba(152, 72, 199, 0.3)",
          fill: false,
          tension: 0.3,
        },
        {
          label: `Trend (${selectedYear - 1})`,
          data: previousData,
          type: "line",
          borderColor: "#aaa",
          backgroundColor: "rgba(180, 180, 180, 0.3)",
          fill: false,
          tension: 0.3,
        },
      ],
    });
  }, [data, selectedMonth, selectedYear]);

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="daily-purchase-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="daily-purchase-tooltip"
        place="left"
        content={
          <>
            <strong>Zakupy dzienne</strong>
            <br />
            Porównanie dziennej wartości zakupów w miesiącu <br />
            {monthNamesPL[selectedMonth - 1]} {selectedYear} i {selectedYear - 1}.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Miesiąc:</label>
          <select value={selectedMonth || ""} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
            {availableFilters.months.map((m) => (
              <option key={m} value={m}>{monthNamesPL[m - 1]}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Rok:</label>
          <select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {availableFilters.years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Zakupy (PLN)" },
                ticks: {
                  callback: (v) => Number(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " "),
                },
              },
            },
            plugins: {
              title: {
                display: true,
                text: `Zakupy dzienne w miesiącu – ${monthNamesPL[selectedMonth - 1]} ${selectedYear}`,
              },
              legend: { display: true },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const val = context.parsed.y || 0;
                    return `${context.dataset.label}: ${val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}`;
                  },
                },
              },
            },
          }}
        />
      ) : (
        <p>Ładowanie danych...</p>
      )}
    </ChartWrapper>
  );
};

export default DailyPurchaseComparisonChart;
